import React from 'react';
import { Box, Typography } from '@mui/material';

interface AircraftGaugeProps {
  value: number; // Expected as 0-100 percentage
  label: string;
  size?: number;
  showDescription?: boolean;
  description?: string;
}

const AircraftGauge: React.FC<AircraftGaugeProps> = ({
  value,
  label,
  size = 120,
  showDescription = false,
  description = ''
}) => {
  const percentage = Math.min(100, Math.max(0, value));
  const isInControl = percentage >= 60;

  // Calculate angle for the arc (270 degree sweep like aircraft gauge)
  // -225 degrees (bottom left) to 45 degrees (bottom right)
  const startAngle = -225;
  const endAngle = 45;
  const angleRange = endAngle - startAngle;
  // Add 90 degree offset because needle is drawn pointing up (negative y)
  // but needs to rotate from bottom-left to bottom-right
  const valueAngle = startAngle + (percentage / 100) * angleRange + 90;

  // SVG calculations
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.42;

  // Create arc path
  const createArcPath = (startDeg: number, endDeg: number, r: number) => {
    const start = (startDeg * Math.PI) / 180;
    const end = (endDeg * Math.PI) / 180;

    const x1 = centerX + r * Math.cos(start);
    const y1 = centerY + r * Math.sin(start);
    const x2 = centerX + r * Math.cos(end);
    const y2 = centerY + r * Math.sin(end);

    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // Determine color based on value
  const getZoneColor = (val: number) => {
    if (val >= 80) return '#22c55e'; // Green
    if (val >= 60) return '#84cc16'; // Light green
    if (val >= 40) return '#fbbf24'; // Yellow
    if (val >= 20) return '#fb923c'; // Orange
    return '#ef4444'; // Red
  };

  const needleColor = getZoneColor(percentage);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      width: size * 1.2,
    }}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: 'visible' }}
        >
          {/* Gradient definitions */}
          <defs>
            <radialGradient id={`gaugeGradient-${label}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#f8f9fa" />
              <stop offset="100%" stopColor="#e9ecef" />
            </radialGradient>
            <filter id={`shadow-${label}`}>
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
            </filter>
          </defs>

          {/* Outer ring with metallic effect */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius + size * 0.05}
            fill="url(#gaugeGradient-${label})"
            stroke="#dee2e6"
            strokeWidth="1"
            filter={`url(#shadow-${label})`}
          />

          {/* Inner background circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="#ffffff"
            stroke="#dee2e6"
            strokeWidth="0.5"
          />

          {/* Color zones on the arc */}
          {/* Red zone (0-20) */}
          <path
            d={createArcPath(-225, -171, radius - size * 0.02)}
            fill="none"
            stroke="#ef4444"
            strokeWidth={size * 0.03}
            strokeLinecap="butt"
            opacity="0.8"
          />

          {/* Orange zone (20-40) */}
          <path
            d={createArcPath(-171, -117, radius - size * 0.02)}
            fill="none"
            stroke="#fb923c"
            strokeWidth={size * 0.03}
            strokeLinecap="butt"
            opacity="0.8"
          />

          {/* Yellow zone (40-60) */}
          <path
            d={createArcPath(-117, -63, radius - size * 0.02)}
            fill="none"
            stroke="#fbbf24"
            strokeWidth={size * 0.03}
            strokeLinecap="butt"
            opacity="0.8"
          />

          {/* Light green zone (60-80) */}
          <path
            d={createArcPath(-63, -9, radius - size * 0.02)}
            fill="none"
            stroke="#84cc16"
            strokeWidth={size * 0.03}
            strokeLinecap="butt"
            opacity="0.8"
          />

          {/* Green zone (80-100) */}
          <path
            d={createArcPath(-9, 45, radius - size * 0.02)}
            fill="none"
            stroke="#22c55e"
            strokeWidth={size * 0.03}
            strokeLinecap="butt"
            opacity="0.8"
          />

          {/* Major tick marks and numbers */}
          {[0, 20, 40, 60, 80, 100].map((num) => {
            const angle = startAngle + (num / 100) * angleRange;
            const radian = (angle * Math.PI) / 180;
            const innerRadius = radius - size * 0.08;
            const outerRadius = radius - size * 0.02;
            const labelRadius = radius - size * 0.18;

            const x1 = centerX + innerRadius * Math.cos(radian);
            const y1 = centerY + innerRadius * Math.sin(radian);
            const x2 = centerX + outerRadius * Math.cos(radian);
            const y2 = centerY + outerRadius * Math.sin(radian);
            const labelX = centerX + labelRadius * Math.cos(radian);
            const labelY = centerY + labelRadius * Math.sin(radian);

            return (
              <g key={`major-${num}`}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={num === 60 ? '#fbbf24' : '#495057'}
                  strokeWidth={num === 60 ? 2 : 1.5}
                  opacity="0.9"
                />
                <text
                  x={labelX}
                  y={labelY}
                  fill={num === 60 ? '#fbbf24' : '#212529'}
                  fontSize={size * 0.09}
                  fontWeight={num === 60 ? 'bold' : 'normal'}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontFamily: 'monospace' }}
                >
                  {num}
                </text>
              </g>
            );
          })}

          {/* Minor tick marks */}
          {[10, 30, 50, 70, 90].map((num) => {
            const angle = startAngle + (num / 100) * angleRange;
            const radian = (angle * Math.PI) / 180;
            const innerRadius = radius - size * 0.06;
            const outerRadius = radius - size * 0.02;

            const x1 = centerX + innerRadius * Math.cos(radian);
            const y1 = centerY + innerRadius * Math.sin(radian);
            const x2 = centerX + outerRadius * Math.cos(radian);
            const y2 = centerY + outerRadius * Math.sin(radian);

            return (
              <line
                key={`minor-${num}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#999"
                strokeWidth="0.8"
                opacity="0.7"
              />
            );
          })}

          {/* Even smaller tick marks for every 5 units */}
          {[5, 15, 25, 35, 45, 55, 65, 75, 85, 95].map((num) => {
            const angle = startAngle + (num / 100) * angleRange;
            const radian = (angle * Math.PI) / 180;
            const innerRadius = radius - size * 0.04;
            const outerRadius = radius - size * 0.02;

            const x1 = centerX + innerRadius * Math.cos(radian);
            const y1 = centerY + innerRadius * Math.sin(radian);
            const x2 = centerX + outerRadius * Math.cos(radian);
            const y2 = centerY + outerRadius * Math.sin(radian);

            return (
              <line
                key={`tiny-${num}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#666"
                strokeWidth="0.5"
                opacity="0.5"
              />
            );
          })}

          {/* 60% threshold marker */}
          <g>
            <line
              x1={centerX + (radius - size * 0.1) * Math.cos((-63 * Math.PI) / 180)}
              y1={centerY + (radius - size * 0.1) * Math.sin((-63 * Math.PI) / 180)}
              x2={centerX + (radius + size * 0.02) * Math.cos((-63 * Math.PI) / 180)}
              y2={centerY + (radius + size * 0.02) * Math.sin((-63 * Math.PI) / 180)}
              stroke="#fbbf24"
              strokeWidth="3"
            />
            {/* Triangle marker */}
            <polygon
              points={`
                ${centerX + (radius + size * 0.03) * Math.cos((-63 * Math.PI) / 180)},${centerY + (radius + size * 0.03) * Math.sin((-63 * Math.PI) / 180)}
                ${centerX + (radius + size * 0.08) * Math.cos((-63 * Math.PI) / 180) - 3},${centerY + (radius + size * 0.08) * Math.sin((-63 * Math.PI) / 180) + 3}
                ${centerX + (radius + size * 0.08) * Math.cos((-63 * Math.PI) / 180) + 3},${centerY + (radius + size * 0.08) * Math.sin((-63 * Math.PI) / 180) + 3}
              `}
              fill="#fbbf24"
            />
          </g>

          {/* Additional gradient definitions for needle */}
          <defs>
            <linearGradient id={`needleGradient-${label}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ff6b6b" />
              <stop offset="30%" stopColor="#dc2626" />
              <stop offset="60%" stopColor="#b91c1c" />
              <stop offset="100%" stopColor="#7f1d1d" />
            </linearGradient>
            <radialGradient id={`hubGradient-${label}`} cx="50%" cy="30%" r="60%">
              <stop offset="0%" stopColor="#868e96" />
              <stop offset="100%" stopColor="#495057" />
            </radialGradient>
          </defs>

          {/* Realistic Needle */}
          <g transform={`translate(${centerX}, ${centerY})`}>
            {/* Needle shadow for depth */}
            <g transform={`rotate(${valueAngle + 1})`}>
              <polygon
                points={`0,8 2,4 1,${-radius * 0.8} 0,${-radius * 0.82} -1,${-radius * 0.8} -2,4`}
                fill="rgba(0,0,0,0.4)"
              />
            </g>

            {/* Main needle body */}
            <g transform={`rotate(${valueAngle})`}>
              {/* Needle base (wider part) */}
              <ellipse cx="0" cy="0" rx={size * 0.015} ry={size * 0.04}
                fill="#991b1b" />

              {/* Needle shaft with taper */}
              <polygon
                points={`0,${size * 0.03} ${size * 0.008},0 ${size * 0.004},${-radius * 0.65} 0,${-radius * 0.82} -${size * 0.004},${-radius * 0.65} -${size * 0.008},0`}
                fill="url(#needleGradient-${label})"
                stroke="#7f1d1d"
                strokeWidth="0.5"
              />

              {/* Needle tip highlight */}
              <polygon
                points={`0,${-radius * 0.82} ${size * 0.002},${-radius * 0.75} -${size * 0.002},${-radius * 0.75}`}
                fill="#fca5a5"
                opacity="0.9"
              />
            </g>

            {/* Center hub with metallic effect */}
            <circle cx="0" cy="0" r={size * 0.055}
              fill="url(#hubGradient-${label})"
              stroke="#303030" strokeWidth="0.5" />
            <circle cx="0" cy="0" r={size * 0.045}
              fill="#495057"
              stroke="#6c757d" strokeWidth="0.3" />
            <circle cx="0" cy="0" r={size * 0.035}
              fill="#343a40" />
            <circle cx="0" cy="0" r={size * 0.015}
              fill="#495057" />
            <circle cx="0" cy="0" r={size * 0.008}
              fill="#6c757d" />
          </g>

          {/* Digital value display */}
          <rect
            x={centerX - size * 0.18}
            y={centerY + size * 0.2}
            width={size * 0.36}
            height={size * 0.12}
            fill="#f8f9fa"
            stroke="#adb5bd"
            strokeWidth="1"
            rx="2"
          />
          <text
            x={centerX}
            y={centerY + size * 0.26}
            fill="#2e7d32"
            fontSize={size * 0.1}
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
          >
            {percentage.toFixed(0)}
          </text>

          {/* Status indicator lights */}
          <circle
            cx={centerX - size * 0.15}
            cy={centerY + size * 0.35}
            r={size * 0.02}
            fill={percentage < 60 ? '#ef4444' : '#dee2e6'}
            stroke="#404040"
            strokeWidth="0.5"
          />
          <circle
            cx={centerX}
            cy={centerY + size * 0.35}
            r={size * 0.02}
            fill={percentage >= 40 && percentage < 80 ? '#fbbf24' : '#dee2e6'}
            stroke="#404040"
            strokeWidth="0.5"
          />
          <circle
            cx={centerX + size * 0.15}
            cy={centerY + size * 0.35}
            r={size * 0.02}
            fill={percentage >= 60 ? '#22c55e' : '#dee2e6'}
            stroke="#404040"
            strokeWidth="0.5"
          />
        </svg>
      </Box>

      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'text.primary',
          fontSize: size > 150 ? '0.85rem' : '0.7rem',
          fontWeight: 600,
          maxWidth: '100%',
          mt: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {label}
      </Typography>

      {/* Description */}
      {showDescription && description && (
        <Typography
          variant="caption"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: size > 150 ? '0.6rem' : '0.55rem',
            fontStyle: 'italic',
            maxWidth: '100%',
            lineHeight: 1.2,
            px: 1
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default AircraftGauge;